import { SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentSaveDiscountViewModel } from '@/shared/api/generated';
import { Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';

import { useDiscountTypeDefaults, useDiscountTypesDropdown } from '../../../hooks/usePlanLookups';
import { CompanyAutocomplete } from '../../molecules/LookupAutocomplete';
import { PlanFormModal, SELECT_ABOVE_MODAL } from '../../molecules/PlanFormModal';

/** Props de `AddDiscountModal`. */
export interface AddDiscountModalProps {
  /** Controla a visibilidade. */
  readonly open: boolean;
  /** Recebe o desconto montado; a persistência é do Salvar do plano. */
  readonly onAdd: (discount: IndividualPaymentSaveDiscountViewModel) => void;
  /** Fecha sem adicionar. */
  readonly onClose: () => void;
}

/**
 * Modal de inclusão de desconto no plano.
 *
 * **Não chama endpoint.** `discounts[]` faz parte do corpo do `PUT` do plano, e
 * não tem rota própria — o modal só monta o item e devolve para o formulário,
 * que persiste no Salvar. É a contraparte da assimetria com cupom, que grava na
 * hora.
 *
 * A empresa conveniada aparece somente quando o tipo escolhido tem
 * `mustSelectAssociatedCompany`, e nesse caso passa a ser obrigatória: é o
 * próprio backend que sinaliza quais tipos de desconto exigem convênio.
 *
 * Fica sempre montado e é fechado por `open → false`: desmontar um dialog Radix
 * aberto deixa `pointer-events: none` no `body` e trava a página.
 */
export function AddDiscountModal({ open, onAdd, onClose }: AddDiscountModalProps) {
  const { t } = useTranslation('individualPayment');
  const { discountTypes } = useDiscountTypesDropdown();

  const [discountTypeId, setDiscountTypeId] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [percentage, setPercentage] = useState('');
  const [associatedCompanyId, setAssociatedCompanyId] = useState<number | null>(null);
  // O autocomplete exibe o rótulo escolhido; o payload leva só o id.
  const [associatedCompanyLabel, setAssociatedCompanyLabel] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { percentage: suggestedPercentage } = useDiscountTypeDefaults(discountTypeId);

  const selectedType = discountTypes.find(option => option.id === discountTypeId);
  const requiresCompany = selectedType?.mustSelectAssociatedCompany === true;

  // O percentual do tipo é sugestão: preenche o campo vazio e sai do caminho se
  // o operador já digitou algo.
  useEffect(() => {
    if (suggestedPercentage !== null && percentage === '') {
      setPercentage(String(suggestedPercentage));
    }
  }, [suggestedPercentage, percentage]);

  const reset = () => {
    setDiscountTypeId(null);
    setDescription('');
    setPercentage('');
    setAssociatedCompanyId(null);
    setAssociatedCompanyLabel('');
    setSubmitted(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const parsedPercentage = Number(percentage.replace(',', '.'));
  const errors = {
    type: discountTypeId === null,
    description: description.trim() === '',
    percentage: percentage.trim() === '' || !Number.isFinite(parsedPercentage),
    company: requiresCompany && associatedCompanyId === null,
  };
  const hasError = Object.values(errors).some(Boolean);

  const handleConfirm = () => {
    setSubmitted(true);
    if (hasError) return;

    // Spread condicional em vez de `?? undefined`: com
    // `exactOptionalPropertyTypes`, passar `undefined` explícito não satisfaz
    // uma propriedade opcional.
    onAdd({
      ...(discountTypeId === null ? {} : { discountTypeId }),
      ...(associatedCompanyId === null ? {} : { associatedCompanyId }),
      description: description.trim(),
      percentage: parsedPercentage,
      active: true,
    });
    reset();
  };

  /** Erro só depois da primeira tentativa: não acusa campo intocado. */
  const showError = (field: keyof typeof errors) => submitted && errors[field];

  const requiredMessage = t('planEdit.validation.required');

  return (
    <PlanFormModal
      open={open}
      title={t('planEdit.values.discounts.modalTitle')}
      description={t('planEdit.values.discounts.pendingSave')}
      confirmLabel={t('planEdit.values.discounts.modalConfirm')}
      onConfirm={handleConfirm}
      onClose={close}
    >
      <SelectInput
        label={`${t('planEdit.values.discounts.type')} *`}
        size="md"
        fullWidth
        placeholder={t('planEdit.values.plan.selectPlaceholder')}
        custom={{ content: SELECT_ABOVE_MODAL }}
        hasError={showError('type')}
        {...(showError('type') ? { helper: requiredMessage } : {})}
        value={discountTypeId !== null ? String(discountTypeId) : ''}
        onValueChange={value => {
          setDiscountTypeId(Number(value));
          // Limpa para a sugestão do novo tipo poder entrar.
          setPercentage('');
          setAssociatedCompanyId(null);
          setAssociatedCompanyLabel('');
        }}
      >
        {discountTypes.map(option => (
          <SelectInputItem
            custom={{ root: 'text-nowrap' }}
            key={option.id}
            value={String(option.id)}
          >
            {option.label}
          </SelectInputItem>
        ))}
      </SelectInput>

      {requiresCompany ? (
        // Autocomplete, e não select: o lookup de empresas conveniadas é
        // server-side por nome (`filter`), sem operação que liste todas. O select
        // ficava permanentemente vazio porque o hook de busca só dispara com três
        // caracteres digitados, e aqui não havia onde digitar.
        <div className="flex flex-col gap-quark">
          <CompanyAutocomplete
            label={`${t('planEdit.values.discounts.company')} *`}
            placeholder={t('planEdit.values.discounts.companyPlaceholder')}
            value={associatedCompanyId !== null ? String(associatedCompanyId) : ''}
            selectedLabel={associatedCompanyLabel}
            hasError={showError('company')}
            optionListClassName={SELECT_ABOVE_MODAL}
            onChange={(id, itemLabel) => {
              setAssociatedCompanyId(id === '' ? null : Number(id));
              setAssociatedCompanyLabel(itemLabel);
            }}
          />
          <span className="ts-caption-xxs text-ctx-content-placeholder">
            {t('planEdit.values.discounts.companyRequired')}
          </span>
        </div>
      ) : null}

      <FormField
        label={t('planEdit.values.discounts.description')}
        required
        {...(showError('description') ? { error: requiredMessage } : {})}
      >
        <Textarea
          rows={3}
          value={description}
          onChange={event => setDescription(event.target.value)}
        />
      </FormField>

      <TextField
        label={`${t('planEdit.values.discounts.percentage')} *`}
        size="md"
        fullWidth
        custom={{ root: 'w-full', input: 'w-full' }}
        placeholder={t('planEdit.placeholders.percentage')}
        hasError={showError('percentage')}
        {...(showError('percentage') ? { helper: requiredMessage } : {})}
        value={percentage}
        onChange={event => setPercentage(event.target.value)}
      />
    </PlanFormModal>
  );
}
